import type { AccountSettings } from "@/generated/api";
import { colorSchemeStorageKey } from "@/theme/color-scheme";
import { readPlayerPrefs, updatePlayerPrefs } from "./playerPrefs";

/** The listener's settings as the account document holds them (see the API's AccountSettings) */
export type SettingsDocument = AccountSettings & Record<string, unknown>;

type Json = Record<string, unknown>;

const isObject = (value: unknown): value is Json =>
    typeof value === "object" && value !== null && !Array.isArray(value);

/** The keys of `over` laid on `base`, object by object: where both have a value, `over` wins */
export const deepMerge = (base: Json, over: Json): Json => {
    const out: Json = { ...base };
    for (const [key, value] of Object.entries(over)) {
        if (value === undefined) continue;
        out[key] = isObject(value) && isObject(out[key]) ? deepMerge(out[key], value) : value;
    }
    return out;
};

/**
 * What the device has that the account does not, key by key. These are copied to the account at sign-in;
 * where the account already has a value, it stays (the account wins).
 */
export const missingFrom = (account: Json, device: Json): Json => {
    const out: Json = {};
    for (const [key, value] of Object.entries(device)) {
        if (value === undefined) continue;
        if (isObject(value)) {
            const inner = missingFrom(isObject(account[key]) ? (account[key] as Json) : {}, value);
            if (Object.keys(inner).length > 0) out[key] = inner;
        } else if (account[key] === undefined) {
            out[key] = value;
        }
    }
    return out;
};

/** The app's colour scheme, as the settings name it: "auto" on the device is "system" in the account */
const schemeToTheme = { light: "light", dark: "dark", auto: "system" } as const;
const themeToScheme = { light: "light", dark: "dark", system: "auto" } as const;

/** The theme the device has chosen, if it has chosen one */
const readTheme = (): AccountSettings["theme"] | undefined => {
    try {
        const stored = localStorage.getItem(colorSchemeStorageKey);
        return stored && stored in schemeToTheme ? schemeToTheme[stored as keyof typeof schemeToTheme] : undefined;
    } catch {
        return undefined;
    }
};

/** The settings the device holds, in the account's shape. Only what the device has chosen is included. */
export const documentFromDevice = (): SettingsDocument => {
    const prefs = readPlayerPrefs();
    const doc: SettingsDocument = {};
    const theme = readTheme();
    if (theme) doc.theme = theme;

    const player: Json = {};
    if (prefs.rate !== undefined) player.speedDefault = prefs.rate;
    if (prefs.autoAdvance !== undefined) player.autoAdvance = prefs.autoAdvance;
    if (Object.keys(player).length > 0) doc.player = player as AccountSettings["player"];

    const shows = Object.entries(prefs.rates ?? {});
    if (shows.length > 0) {
        doc.show = Object.fromEntries(shows.map(([id, speed]) => [id, { speed }]));
    }
    return doc;
};

/**
 * Writes the settings to the device, so the player and the colour scheme use them. A key the document does not
 * have is taken away from the device.
 */
export const applyToDevice = (doc: SettingsDocument) => {
    const shows = Object.entries(doc.show ?? {}).flatMap(([id, show]) =>
        show.speed !== undefined ? [[id, show.speed] as const] : []
    );
    updatePlayerPrefs({
        rate: doc.player?.speedDefault,
        autoAdvance: doc.player?.autoAdvance,
        rates: shows.length > 0 ? Object.fromEntries(shows) : undefined,
    });
    try {
        if (doc.theme) {
            localStorage.setItem(colorSchemeStorageKey, themeToScheme[doc.theme]);
        } else {
            localStorage.removeItem(colorSchemeStorageKey);
        }
    } catch {
        // Blocked: the colour scheme then goes back to the system's on the next visit
    }
};

/** The Mantine colour scheme for a theme in the settings */
export const schemeFor = (theme: AccountSettings["theme"] | undefined) => themeToScheme[theme ?? "system"];

/**
 * A change as the API applies it: keys given are set, a key given as null is removed (also inside objects),
 * and the rest stay. Returns a new object.
 */
export const applyChange = (base: Json, change: Json): Json => {
    const out: Json = { ...base };
    for (const [key, value] of Object.entries(change)) {
        if (value === null) {
            delete out[key];
        } else if (isObject(value)) {
            out[key] = applyChange(isObject(out[key]) ? (out[key] as Json) : {}, value);
        } else {
            out[key] = value;
        }
    }
    return out;
};
